import React, { createContext, useContext, useCallback, useState } from 'react';
import styles from './DropZone.module.scss';
import dragStyles from './DragAndDropFileUploader.module.scss';
import { Icon, IconList, Text } from 'components/core';

type DropZoneProps = {
  /** Child components to render inside the drop zone */
  children?: React.ReactNode;
};

type HasDroppedFiles = {
  files: File[];
  any: boolean;
  clearFiles: () => void;
  isDialogOpenFromDrop: boolean;
  closeDialog: () => void;
};

const DropZoneContext = createContext<HasDroppedFiles>({
  files: [],
  any: false,
  clearFiles: () => {
    // This is a no-op for the default context
  },
  isDialogOpenFromDrop: false,
  closeDialog: () => {
    // This is a no-op for the default context
  },
});

export const useDropZoneContext = () => {
  return useContext(DropZoneContext);
};

/**
 * A drag and drop file upload component that provides visual feedback
 * when files are dragged over the component area.
 */
const DropZone = ({ children }: DropZoneProps) => {
  const [dropFiles, setDropFiles] = useState<File[]>([]);
  const [isDialogOpenFromDrop, setIsDialogOpenFromDrop] = useState(false);

  const clearFiles = useCallback(() => {
    setDropFiles([]);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpenFromDrop(false);
  }, []);

  const contextValue: HasDroppedFiles = {
    files: dropFiles,
    any: dropFiles.length > 0,
    clearFiles,
    isDialogOpenFromDrop,
    closeDialog,
  };

  const [isDragActive, setIsDragActive] = useState(false);
  // Counter to prevent overlay flickering when dragging over child elements
  const dragCounterRef = React.useRef(0);

  const isPdfFile = useCallback((file: File): boolean => {
    return (
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    );
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Increment counter for each drag enter event
    dragCounterRef.current++;

    // Only show drop zone for files
    // Safari doesn't provide access to files during dragenter/dragover
    // So we show the overlay for any file drag and validate during drop
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragActive(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Decrement counter for each drag leave event
    dragCounterRef.current--;

    // Only hide when we've left all drag targets (counter reaches 0)
    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset counter and hide overlay after drop
      dragCounterRef.current = 0;
      setIsDragActive(false);

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const validPdfFiles = Array.from(files).filter(isPdfFile);

        if (validPdfFiles.length > 0) {
          setDropFiles(validPdfFiles);
          setIsDialogOpenFromDrop(true);
        }
      }
    },
    [isPdfFile]
  );

  // Reset drag counter on component unmount and handle potential edge cases
  React.useEffect(() => {
    return () => {
      // Reset drag counter to prevent memory leaks
      dragCounterRef.current = 0;
      // Reset drag state if component unmounts while dragging
      setIsDragActive(false);
    };
  }, []);

  return (
    <DropZoneContext.Provider value={contextValue}>
      <div
        className={styles.container}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="region"
        aria-label="File drop zone"
      >
        {children}
        {isDragActive && (
          <div
            className={`${dragStyles.dropZone} ${dragStyles.dragged} ${styles.overlay}`}
            role="status"
            aria-live="polite"
          >
            <Text className={styles.instructions}>
              <Icon
                icon={IconList.solid.faCloudUploadAlt}
                size="xxl"
                className={styles.icon}
              />
              Drop PDF to upload
            </Text>
          </div>
        )}
      </div>
    </DropZoneContext.Provider>
  );
};

export default DropZone;
